/**
 * Client-side inactivity logout — the precise enforcer of "logged out after N
 * minutes of no activity" (Design B).
 *
 * The server also expires the session after the same idle window, but that only
 * bites on the next request. This timer makes the logout happen *at* the idle
 * limit even if the user never clicks again, so the app doesn't sit there looking
 * logged in on stale offline-cached data.
 *
 * Crucially, the window is reset by REAL user activity only (pointer/key/scroll),
 * never by background traffic — a blind background refresh must not keep the
 * session alive and defeat the timeout.
 */

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'click', 'scroll', 'touchstart', 'wheel'] as const;
const DEFAULT_IDLE_MS = 60 * 60 * 1000; // 60 minutes — must match SESSION_IDLE_WINDOW
const RESCHEDULE_THROTTLE_MS = 1000; // don't reschedule the timer more than 1×/sec

export interface IdleLogoutOptions {
  /** Idle window in ms before logout fires. Default 60 min. */
  idleMs?: number;
  /** Called once when the idle limit is reached. Do the logout here. */
  onIdle: () => void;
  /** Optional guard — return false to pause the timer (e.g. not authenticated). */
  isActive?: () => boolean;
}

/**
 * Begin watching for inactivity. Returns a stop() cleanup that removes all
 * listeners and cancels the timer.
 */
export function startIdleLogout({ idleMs = DEFAULT_IDLE_MS, onIdle, isActive }: IdleLogoutOptions): () => void {
  let lastActivity = Date.now();
  let lastReschedule = 0;
  let fired = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const check = () => {
    if (fired) return;
    if (isActive && !isActive()) {
      schedule(); // paused (not authed) — re-arm and re-check later
      return;
    }
    if (Date.now() - lastActivity >= idleMs) {
      fired = true;
      onIdle();
      return;
    }
    schedule();
  };

  function schedule() {
    if (timer) clearTimeout(timer);
    const remaining = Math.max(0, idleMs - (Date.now() - lastActivity));
    timer = setTimeout(check, remaining || idleMs);
  }

  const onActivity = () => {
    if (fired) return;
    const now = Date.now();
    lastActivity = now;
    if (now - lastReschedule > RESCHEDULE_THROTTLE_MS) {
      lastReschedule = now;
      schedule();
    }
  };

  // On tab focus / return-from-sleep, timers may not have fired — check immediately.
  const onVisible = () => {
    if (document.visibilityState === 'visible') check();
  };

  ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', onVisible);
  schedule();

  return () => {
    if (timer) clearTimeout(timer);
    ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', onVisible);
  };
}
